import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Label } from '../ui/label';
import { adminService, authService } from '../../lib/api';
import PasswordInput from '../ui/PasswordInput';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const UtilisateursTab = () => {
  const [utilisateurs, setUtilisateurs] = useState([]);
  const [actionLogs, setActionLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [logsLoading, setLogsLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [activeTab, setActiveTab] = useState('users'); // 'users' ou 'logs'
  
  // Obtenir l'utilisateur connecté
  const currentUser = authService.getCurrentUser();
  const isSuperAdmin = currentUser?.role === 'SUPER_ADMIN';
  
  // Form states
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'COMPTABLE'
  });

  // Logs filter
  const [logsFilter, setLogsFilter] = useState({
    admin_id: '',
    action_type: ''
  });

  const formatDate = (dateString) => {
    if (!dateString) return 'Jamais';
    return new Date(dateString).toLocaleString('fr-FR');
  };

  useEffect(() => {
    fetchUtilisateurs();
  }, []);

  const fetchUtilisateurs = async () => {
    try {
      setLoading(true);
      const data = await adminService.getAdminUsers();
      setUtilisateurs(data.utilisateurs || []);
    } catch (error) {
      console.error('Erreur lors du chargement des utilisateurs:', error);
      toast.error('Erreur lors du chargement des utilisateurs');
    } finally {
      setLoading(false);
    }
  };

  const fetchActionLogs = async () => {
    if (!isSuperAdmin) {
      toast.warning('Accès réservé aux Super Administrateurs');
      return;
    }

    try {
      setLogsLoading(true);
      const data = await adminService.getAdminActionLogs({
        ...logsFilter,
        limit: 100
      });
      setActionLogs(data.logs || []);
    } catch (error) {
      console.error('Erreur lors du chargement des logs:', error);
      toast.error('Erreur lors du chargement de l\'historique');
    } finally {
      setLogsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'logs' && isSuperAdmin) {
      fetchActionLogs();
    }
  }, [activeTab, logsFilter]);

  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    if (!isSuperAdmin) {
      toast.error('Seuls les Super Administrateurs peuvent créer des comptes');
      return;
    }
    
    // Validations
    if (!formData.nom || !formData.prenom || !formData.email || !formData.password) {
      toast.warning('Veuillez remplir tous les champs obligatoires');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    try {
      await adminService.createAdminUser({
        nom: formData.nom,
        prenom: formData.prenom,
        email: formData.email,
        password: formData.password,
        role: formData.role
      });
      
      toast.success('✅ Administrateur créé avec succès !');
      setShowCreateModal(false);
      resetForm();
      fetchUtilisateurs();
    } catch (error) {
      console.error('Erreur lors de la création:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la création de l\'administrateur');
    }
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    
    if (!selectedUser) return;

    if (!isSuperAdmin) {
      toast.error('Seuls les Super Administrateurs peuvent modifier des comptes');
      return;
    }

    try {
      await adminService.updateAdminUser(selectedUser.id, {
        nom: formData.nom,
        prenom: formData.prenom,
        role: formData.role,
        is_active: formData.is_active
      });
      
      toast.success('✅ Administrateur modifié avec succès !');
      setShowEditModal(false);
      setSelectedUser(null);
      resetForm();
      fetchUtilisateurs();
    } catch (error) {
      console.error('Erreur lors de la modification:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la modification de l\'administrateur');
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;

    if (!isSuperAdmin) {
      toast.error('Seuls les Super Administrateurs peuvent supprimer des comptes');
      return;
    }

    try {
      await adminService.deleteAdminUser(selectedUser.id);
      
      toast.success('✅ Administrateur supprimé avec succès !');
      setShowDeleteModal(false);
      setSelectedUser(null);
      fetchUtilisateurs();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression de l\'administrateur');
    }
  };

  const openEditModal = (user) => {
    if (!isSuperAdmin) {
      toast.warning('Seuls les Super Administrateurs peuvent modifier des comptes');
      return;
    }
    setSelectedUser(user);
    setFormData({
      nom: user.nom,
      prenom: user.prenom,
      email: user.email,
      role: user.role,
      is_active: user.is_active,
      password: '',
      confirmPassword: ''
    });
    setShowEditModal(true);
  };

  const openDeleteModal = (user) => {
    if (!isSuperAdmin) {
      toast.warning('Seuls les Super Administrateurs peuvent supprimer des comptes');
      return;
    }
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const openLogsModal = async (user) => {
    if (!isSuperAdmin) {
      toast.warning('Seuls les Super Administrateurs peuvent consulter l\'historique');
      return;
    }
    setSelectedUser(user);
    setLogsLoading(true);
    setShowLogsModal(true);
    
    try {
      const data = await adminService.getAdminUserLogs(user.id, { limit: 50 });
      setActionLogs(data.logs || []);
    } catch (error) {
      console.error('Erreur lors du chargement des logs:', error);
      toast.error('Erreur lors du chargement de l\'historique');
    } finally {
      setLogsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      nom: '',
      prenom: '',
      email: '',
      password: '',
      confirmPassword: '',
      role: 'COMPTABLE'
    });
  };

  const getRoleBadge = (role) => {
    const config = {
      'SUPER_ADMIN': { color: 'bg-purple-100 text-purple-800', icon: '👑' },
      'ADMIN_STANDARD': { color: 'bg-blue-100 text-blue-800', icon: '🔑' },
      'COMPTABLE': { color: 'bg-green-100 text-green-800', icon: '🧮' }
    };
    return config[role] || config['ADMIN_STANDARD'];
  };

  const getActionBadge = (action) => {
    const config = {
      'CREATE_ADMIN': { color: 'bg-green-100 text-green-800', icon: '➕', label: 'Création' },
      'UPDATE_ADMIN': { color: 'bg-blue-100 text-blue-800', icon: '✏️', label: 'Modification' },
      'DELETE_ADMIN': { color: 'bg-red-100 text-red-800', icon: '🗑️', label: 'Suppression' },
    };
    return config[action] || { color: 'bg-gray-100 text-gray-800', icon: '📝', label: action };
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <p className="text-gray-600">Chargement des utilisateurs...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <ToastContainer position="top-right" autoClose={3000} />
      
      {/* Tabs Navigation */}
      <div className="flex space-x-1 border-b border-gray-200">
        <button
          className={`px-6 py-3 font-medium text-sm transition-colors ${
            activeTab === 'users'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setActiveTab('users')}
        >
          👥 Gestion des Utilisateurs
        </button>
        {isSuperAdmin && (
          <button
            className={`px-6 py-3 font-medium text-sm transition-colors ${
              activeTab === 'logs'
                ? 'border-b-2 border-blue-600 text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('logs')}
          >
            📋 Historique des Actions
          </button>
        )}
      </div>

      {/* Users Tab */}
      {activeTab === 'users' && (
        <>
          {/* En-tête avec statistiques */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-100 text-sm font-medium">Total Admins</p>
                    <p className="text-3xl font-bold">{utilisateurs.length}</p>
                  </div>
                  <div className="text-4xl opacity-80">👥</div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-green-100 text-sm font-medium">Actifs</p>
                    <p className="text-3xl font-bold">
                      {utilisateurs.filter(u => u.is_active).length}
                    </p>
                  </div>
                  <div className="text-4xl opacity-80">✅</div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-purple-100 text-sm font-medium">Super Admins</p>
                    <p className="text-3xl font-bold">
                      {utilisateurs.filter(u => u.role === 'SUPER_ADMIN').length}
                    </p>
                  </div>
                  <div className="text-4xl opacity-80">👑</div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-green-100 text-sm font-medium">Comptables</p>
                    <p className="text-3xl font-bold">
                      {utilisateurs.filter(u => u.role === 'COMPTABLE').length}
                    </p>
                  </div>
                  <div className="text-4xl opacity-80">🧮</div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-orange-100 text-sm font-medium">Admins Standard</p>
                    <p className="text-3xl font-bold">
                      {utilisateurs.filter(u => u.role === 'ADMIN_STANDARD').length}
                    </p>
                  </div>
                  <div className="text-4xl opacity-80">🔑</div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Liste des utilisateurs */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Administrateurs ALO ({utilisateurs.length})</CardTitle>
                <div className="flex space-x-3">
                  <Button size="sm" variant="outline" onClick={fetchUtilisateurs}>
                    🔄 Actualiser
                  </Button>
                  {isSuperAdmin && (
                    <Button size="sm" onClick={() => setShowCreateModal(true)}>
                      ➕ Nouvel Administrateur
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {utilisateurs.length === 0 ? (
                <div className="text-center py-8">
                  <div className="text-4xl mb-4">👥</div>
                  <p className="text-gray-600">Aucun administrateur enregistré</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full table-auto">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left py-3 px-4 font-medium text-gray-700">Nom Complet</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-700">Email</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-700">Rôle</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-700">Statut</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-700">Dernière Connexion</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-700">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {utilisateurs.map((user) => {
                        const roleBadge = getRoleBadge(user.role);
                        return (
                          <tr key={user.id} className="border-b hover:bg-gray-50">
                            <td className="py-3 px-4">
                              <div className="flex items-center">
                                <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center mr-3">
                                  <span className="text-white font-medium text-sm">
                                    {user.prenom?.[0]}{user.nom?.[0]}
                                  </span>
                                </div>
                                <div>
                                  <div className="font-medium text-gray-900">
                                    {user.prenom} {user.nom}
                                  </div>
                                  <div className="text-xs text-gray-500">ID: {user.id.slice(0, 8)}...</div>
                                </div>
                              </div>
                            </td>
                            <td className="py-3 px-4">
                              <span className="text-sm text-gray-900">{user.email}</span>
                            </td>
                            <td className="py-3 px-4">
                              <span className={`px-3 py-1 rounded-full text-xs font-medium ${roleBadge.color}`}>
                                {roleBadge.icon} {user.role === 'SUPER_ADMIN' ? 'Super Admin' : user.role === 'COMPTABLE' ? 'Comptable' : 'Admin Standard'}
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                user.is_active 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {user.is_active ? '✅ Actif' : '❌ Inactif'}
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              <span className="text-sm text-gray-500">
                                {formatDate(user.derniere_connexion)}
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex space-x-2">
                                {isSuperAdmin && (
                                  <>
                                    <Button 
                                      size="sm" 
                                      variant="outline"
                                      onClick={() => openLogsModal(user)}
                                      title="Historique"
                                    >
                                      📋
                                    </Button>
                                    <Button 
                                      size="sm" 
                                      variant="outline"
                                      onClick={() => openEditModal(user)}
                                      title="Modifier"
                                    >
                                      ✏️
                                    </Button>
                                    <Button 
                                      size="sm" 
                                      variant="outline"
                                      className="border-red-300 text-red-600 hover:bg-red-50"
                                      onClick={() => openDeleteModal(user)}
                                      title="Supprimer"
                                    >
                                      🗑️
                                    </Button>
                                  </>
                                )}
                                {!isSuperAdmin && (
                                  <span className="text-sm text-gray-500 italic">
                                    Accès limité
                                  </span>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Logs Tab */}
      {activeTab === 'logs' && isSuperAdmin && (
        <Card>
          <CardHeader>
            <CardTitle>📋 Historique des Actions Administratives</CardTitle>
          </CardHeader>
          <CardContent>
            {/* Filtres */}
            <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Filtrer par Admin</Label>
                <select
                  value={logsFilter.admin_id}
                  onChange={(e) => setLogsFilter({...logsFilter, admin_id: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="">Tous les admins</option>
                  {utilisateurs.map(u => (
                    <option key={u.id} value={u.id}>{u.prenom} {u.nom}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Filtrer par Action</Label>
                <select
                  value={logsFilter.action_type}
                  onChange={(e) => setLogsFilter({...logsFilter, action_type: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="">Toutes les actions</option>
                  <option value="CREATE_ADMIN">Création</option>
                  <option value="UPDATE_ADMIN">Modification</option>
                  <option value="DELETE_ADMIN">Suppression</option>
                </select>
              </div>
              <div className="flex items-end">
                <Button onClick={fetchActionLogs} className="w-full">
                  🔍 Rechercher
                </Button>
              </div>
            </div>

            {logsLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Chargement de l'historique...</p>
              </div>
            ) : actionLogs.length === 0 ? (
              <div className="text-center py-8">
                <div className="text-4xl mb-4">📋</div>
                <p className="text-gray-600">Aucune action enregistrée</p>
              </div>
            ) : (
              <div className="space-y-3">
                {actionLogs.map((log) => {
                  const actionBadge = getActionBadge(log.action);
                  return (
                    <div key={log.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${actionBadge.color}`}>
                              {actionBadge.icon} {actionBadge.label}
                            </span>
                            <span className="text-sm text-gray-500">
                              {formatDate(log.timestamp)}
                            </span>
                          </div>
                          <p className="text-sm text-gray-900 mb-1">
                            <span className="font-medium">{log.admin_email}</span>
                          </p>
                          {log.details && (
                            <div className="text-sm text-gray-600 mt-2">
                              {log.details.admin_email && (
                                <p>Admin concerné: <span className="font-medium">{log.details.admin_email}</span></p>
                              )}
                              {log.details.admin_role && (
                                <p>Rôle: <span className="font-medium">{log.details.admin_role}</span></p>
                              )}
                              {log.details.changes && (
                                <div className="mt-1">
                                  <p className="font-medium">Modifications:</p>
                                  <pre className="text-xs bg-gray-100 p-2 rounded mt-1">
                                    {JSON.stringify(log.details.changes, null, 2)}
                                  </pre>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Modal Création */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-medium mb-4">➕ Créer un Nouvel Administrateur</h3>
            
            <form onSubmit={handleCreateUser} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="prenom">Prénom *</Label>
                  <Input
                    id="prenom"
                    value={formData.prenom}
                    onChange={(e) => setFormData({...formData, prenom: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="nom">Nom *</Label>
                  <Input
                    id="nom"
                    value={formData.nom}
                    onChange={(e) => setFormData({...formData, nom: e.target.value})}
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                />
              </div>

              <div>
                <Label htmlFor="role">Rôle *</Label>
                <select
                  id="role"
                  value={formData.role}
                  onChange={(e) => setFormData({...formData, role: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="COMPTABLE">🧮 Comptable (accès comptabilité)</option>
                  <option value="ADMIN_STANDARD">👤 Admin Standard (accès limité)</option>
                  <option value="SUPER_ADMIN">⭐ Super Admin (tous droits)</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {formData.role === 'SUPER_ADMIN' 
                    ? '👑 Accès complet: gérer entreprises, employés et créer/modifier/supprimer des admins'
                    : formData.role === 'COMPTABLE'
                    ? '🧮 Accès comptabilité: module financier, écritures comptables uniquement'
                    : '🔑 Accès limité: gérer entreprises et employés uniquement (sans gestion des admins)'}
                </p>
              </div>

              <div>
                <Label htmlFor="password">Mot de passe *</Label>
                <PasswordInput
                  id="password"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  placeholder="Minimum 6 caractères"
                  required
                />
              </div>

              <div>
                <Label htmlFor="confirmPassword">Confirmer le mot de passe *</Label>
                <PasswordInput
                  id="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                  placeholder="Retapez le mot de passe"
                  required
                />
              </div>

              <div className="flex space-x-3 mt-6">
                <Button 
                  type="button"
                  variant="outline" 
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                  }}
                  className="flex-1"
                >
                  Annuler
                </Button>
                <Button 
                  type="submit"
                  className="flex-1"
                >
                  ✅ Créer
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Modification */}
      {showEditModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium mb-4">
              ✏️ Modifier l'Administrateur
            </h3>
            
            <form onSubmit={handleEditUser} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit-prenom">Prénom</Label>
                  <Input
                    id="edit-prenom"
                    value={formData.prenom}
                    onChange={(e) => setFormData({...formData, prenom: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="edit-nom">Nom</Label>
                  <Input
                    id="edit-nom"
                    value={formData.nom}
                    onChange={(e) => setFormData({...formData, nom: e.target.value})}
                  />
                </div>
              </div>

              <div>
                <Label>Email</Label>
                <Input
                  value={formData.email}
                  disabled
                  className="bg-gray-100"
                />
                <p className="text-xs text-gray-500 mt-1">L'email ne peut pas être modifié</p>
              </div>

              <div>
                <Label htmlFor="edit-role">Rôle</Label>
                <select
                  id="edit-role"
                  value={formData.role}
                  onChange={(e) => setFormData({...formData, role: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="ADMIN_STANDARD">Admin Standard (accès limité)</option>
                  <option value="SUPER_ADMIN">Super Admin (tous droits)</option>
                </select>
              </div>

              <div>
                <Label htmlFor="edit-status">Statut</Label>
                <select
                  id="edit-status"
                  value={formData.is_active ? 'true' : 'false'}
                  onChange={(e) => setFormData({...formData, is_active: e.target.value === 'true'})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="true">✅ Actif</option>
                  <option value="false">❌ Inactif</option>
                </select>
              </div>

              <div className="flex space-x-3 mt-6">
                <Button 
                  type="button"
                  variant="outline" 
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedUser(null);
                    resetForm();
                  }}
                  className="flex-1"
                >
                  Annuler
                </Button>
                <Button 
                  type="submit"
                  className="flex-1"
                >
                  💾 Enregistrer
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Suppression */}
      {showDeleteModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <div className="text-red-500 text-2xl mr-3">⚠️</div>
              <h3 className="text-lg font-medium">Supprimer l'Administrateur</h3>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-700 mb-3">
                Êtes-vous sûr de vouloir supprimer cet administrateur ?
              </p>
              <div className="bg-red-50 p-3 rounded-lg border border-red-200">
                <p className="font-medium text-red-900">{selectedUser.prenom} {selectedUser.nom}</p>
                <p className="text-sm text-red-700">{selectedUser.email}</p>
                <p className="text-sm text-red-700">{selectedUser.role}</p>
              </div>
              <div className="mt-3 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-sm text-yellow-800">
                  ⚠️ Cette action est irréversible. Vous ne pourrez pas restaurer cet administrateur.
                </p>
              </div>
            </div>
            
            <div className="flex space-x-3">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedUser(null);
                }}
                className="flex-1"
              >
                Annuler
              </Button>
              <Button 
                onClick={handleDeleteUser}
                className="flex-1 bg-red-600 hover:bg-red-700"
              >
                🗑️ Confirmer la suppression
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Historique Utilisateur */}
      {showLogsModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium">
                📋 Historique de {selectedUser.prenom} {selectedUser.nom}
              </h3>
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => {
                  setShowLogsModal(false);
                  setSelectedUser(null);
                  setActionLogs([]);
                }}
              >
                ✖️ Fermer
              </Button>
            </div>
            
            {logsLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Chargement...</p>
              </div>
            ) : actionLogs.length === 0 ? (
              <div className="text-center py-8">
                <div className="text-4xl mb-4">📋</div>
                <p className="text-gray-600">Aucune action enregistrée</p>
              </div>
            ) : (
              <div className="space-y-3">
                {actionLogs.map((log) => {
                  const actionBadge = getActionBadge(log.action);
                  return (
                    <div key={log.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${actionBadge.color}`}>
                              {actionBadge.icon} {actionBadge.label}
                            </span>
                            <span className="text-sm text-gray-500">
                              {formatDate(log.timestamp)}
                            </span>
                          </div>
                          {log.details && (
                            <div className="text-sm text-gray-600">
                              {log.details.admin_email && (
                                <p>Admin: <span className="font-medium">{log.details.admin_email}</span></p>
                              )}
                              {log.details.changes && (
                                <div className="mt-1">
                                  <p className="font-medium">Changements:</p>
                                  <pre className="text-xs bg-gray-100 p-2 rounded mt-1">
                                    {JSON.stringify(log.details.changes, null, 2)}
                                  </pre>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Avertissement pour admins standard */}
      {!isSuperAdmin && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start">
            <div className="text-yellow-500 text-xl mr-3">⚠️</div>
            <div>
              <h4 className="font-medium text-yellow-900 mb-1">Accès Limité</h4>
              <p className="text-sm text-yellow-800">
                Vous disposez d'un accès <strong>Admin Standard</strong>. Seuls les <strong>Super Administrateurs</strong> peuvent créer, modifier, supprimer des comptes administrateurs ou consulter l'historique des actions.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UtilisateursTab;
